<template>
  <div class="todo-container">
    <h2 class="todo-title">待办事项 (Vue2)</h2>

    <!-- 添加 todo 表单 -->
    <form @submit.prevent="handleAddTodo" class="todo-form">
      <input v-model="newTodoTitle" type="text" placeholder="添加新的待办事项..." :disabled="loading" class="todo-input" />
      <button type="submit" :disabled="loading || !newTodoTitle.trim()" class="todo-button">
        {{ loading ? '添加中...' : '添加' }}
      </button>
    </form>

    <!-- 错误提示 -->
    <div v-if="error" class="error-message">
      {{ error }}
    </div>

    <!-- Todo 列表 -->
    <div v-if="loading && todos.length === 0" class="loading-message">
      加载中...
    </div>
    <div v-else-if="todos.length === 0" class="empty-message">
      暂无待办事项
    </div>
    <ul v-else class="todo-list">
      <li v-for="todo in todos" :key="todo.id" class="todo-item">
        <input type="checkbox" :checked="todo.completed" @change="handleToggleTodo(todo.id, todo.completed)"
          class="todo-checkbox" />
        <span :class="['todo-text', { 'todo-text--completed': todo.completed }]">
          {{ todo.title }}
        </span>
        <button @click="handleDeleteTodo(todo.id)" class="delete-button">
          删除
        </button>
      </li>
    </ul>

    <!-- 统计信息 -->
    <div v-if="todos.length > 0" class="todo-stats">
      总共 {{ todos.length }} 项，已完成 {{ completedCount }} 项
    </div>
  </div>
</template>

<script>
export default {
  name: 'TodoList',
  data() {
    return {
      todos: [],
      newTodoTitle: '',
      loading: false,
      error: null
    }
  },
  computed: {
    completedCount() {
      return this.todos.filter(todo => todo.completed).length
    }
  },
  mounted() {
    this.fetchTodos()
  },
  methods: {
    // 获取所有 todos
    async fetchTodos() {
      try {
        this.loading = true
        const apiUrl = process.env.VUE_APP_API_URL || 'http://localhost:5174'
        const response = await fetch(`${apiUrl}/api/todos`, {
          credentials: 'include'
        })

        if (!response.ok) {
          throw new Error('获取 todos 失败')
        }

        const data = await response.json()
        this.todos = data
        this.error = null
      } catch (err) {
        console.error('获取 todos 失败:', err)
        this.error = err.message
      } finally {
        this.loading = false
      }
    },

    // 创建新的 todo
    async handleAddTodo() {
      if (!this.newTodoTitle.trim()) {
        return
      }

      try {
        this.loading = true
        const apiUrl = process.env.VUE_APP_API_URL || 'http://localhost:5174'
        const response = await fetch(`${apiUrl}/api/todos`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({ title: this.newTodoTitle })
        })

        if (!response.ok) {
          throw new Error('创建 todo 失败')
        }

        const newTodo = await response.json()
        this.todos = [newTodo, ...this.todos]
        this.newTodoTitle = ''
        this.error = null
      } catch (err) {
        console.error('创建 todo 失败:', err)
        this.error = err.message
      } finally {
        this.loading = false
      }
    },

    // 切换完成状态
    async handleToggleTodo(id, completed) {
      try {
        const apiUrl = process.env.VUE_APP_API_URL || 'http://localhost:5174'
        const response = await fetch(`${apiUrl}/api/todos/${id}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({ completed: !completed })
        })

        if (!response.ok) {
          throw new Error('更新 todo 失败')
        }

        const updatedTodo = await response.json()
        this.todos = this.todos.map(todo =>
          todo.id === id ? updatedTodo : todo
        )
        this.error = null
      } catch (err) {
        console.error('更新 todo 失败:', err)
        this.error = err.message
      }
    },

    // 删除 todo
    async handleDeleteTodo(id) {
      try {
        const apiUrl = process.env.VUE_APP_API_URL || 'http://localhost:5174'
        const response = await fetch(`${apiUrl}/api/todos/${id}`, {
          method: 'DELETE',
          credentials: 'include'
        })

        if (!response.ok) {
          throw new Error('删除 todo 失败')
        }

        this.todos = this.todos.filter(todo => todo.id !== id)
        this.error = null
      } catch (err) {
        console.error('删除 todo 失败:', err)
        this.error = err.message
      }
    }
  }
}
</script>

<style scoped>
.todo-container {
  max-width: 600px;
  margin: 0 auto;
  padding: 20px;
}

.todo-title {
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 20px;
  background: linear-gradient(135deg, #42b983 0%, #2d7b5c 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.todo-form {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
}

.todo-input {
  flex: 1;
  padding: 10px;
  border: 2px solid #42b983;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s;
}

.todo-input:focus {
  border-color: #2d7b5c;
}

.todo-input:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.todo-button {
  padding: 10px 20px;
  background: linear-gradient(135deg, #42b983 0%, #2d7b5c 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  transition: opacity 0.2s;
}

.todo-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.error-message {
  padding: 10px;
  margin-bottom: 20px;
  background: #fee;
  border: 1px solid #fcc;
  border-radius: 8px;
  color: #d00;
}

.loading-message,
.empty-message {
  text-align: center;
  color: #999;
  padding: 40px 0;
}

.todo-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.todo-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  margin-bottom: 8px;
  background: white;
  border: 2px solid #42b983;
  border-radius: 8px;
  transition: all 0.2s;
}

.todo-item:hover {
  box-shadow: 0 2px 8px rgba(66, 185, 131, 0.2);
}

.todo-checkbox {
  width: 20px;
  height: 20px;
  cursor: pointer;
  accent-color: #42b983;
}

.todo-text {
  flex: 1;
  font-size: 14px;
  color: #333;
}

.todo-text--completed {
  text-decoration: line-through;
  color: #999;
}

.delete-button {
  padding: 6px 12px;
  background: #ff4444;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
  transition: background 0.2s;
}

.delete-button:hover {
  background: #cc0000;
}

.todo-stats {
  margin-top: 20px;
  padding: 10px;
  text-align: center;
  color: #666;
  font-size: 14px;
}
</style>
